import { BehaviorSubject, filter, lastValueFrom, startWith, switchMap } from 'rxjs';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  BreadcrubmsInfoItem,
  CategoryService,
  IBreadcrumb,
  ProductCategory,
  RouteData,
  SRouterLinkWithHrefDirective,
  TranslatePipe,
} from '@speedy/core';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-breadcrumbs',
  styleUrls: ['./breadcrumbs.component.scss'],
  templateUrl: './breadcrumbs.component.html',
  standalone: true,
  imports: [SRouterLinkWithHrefDirective, AsyncPipe, TranslatePipe],
})
export class BreadcrumbsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly categoryService = inject(CategoryService);
  private readonly router = inject(Router);

  breadcrumbs$ = new BehaviorSubject<IBreadcrumb[]>([]);

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        switchMap(() => this.getBreadcrumbs(this.router.routerState.root)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private async getBreadcrumbs(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: IBreadcrumb[] = [],
  ): Promise<IBreadcrumb[]> {
    const ROUTE_DATA_BREADCRUMB = 'breadcrumb';
    const children = route.children;

    if (children.length === 0) {
      this.breadcrumbs$.next(breadcrumbs);
      return breadcrumbs;
    }

    for (const child of children) {
      if (child.outlet !== PRIMARY_OUTLET) {
        continue;
      }

      if (
        !Object.prototype.hasOwnProperty.call(child.snapshot.data, ROUTE_DATA_BREADCRUMB)
      ) {
        return this.getBreadcrumbs(child, url, breadcrumbs);
      }

      const routeURL = child.snapshot.url.map((segment) => segment.path).join('/');
      const routeParentURL = child.snapshot?.parent?.url
        .map((segment) => segment.path)
        .join('/');

      const label = child.snapshot.data[ROUTE_DATA_BREADCRUMB];

      url += `/${routeURL ? routeURL : routeParentURL}`;

      let breadcrumb: IBreadcrumb = {
        label: child.snapshot.data[ROUTE_DATA_BREADCRUMB],
        url: label === 'product' ? `/product${url}` : url,
      };

      const data = (child.data as BehaviorSubject<RouteData>).value;

      const breadcrumbBlogParent = this.getParentBlogBreadcrumb(data);
      if (breadcrumbBlogParent) {
        breadcrumbs.push(breadcrumbBlogParent);
      }

      try {
        const productBreadcrumbs = await this.getParentProductBreadcrumbs(data);
        if (productBreadcrumbs && Array.isArray(productBreadcrumbs)) {
          productBreadcrumbs.forEach((bc) => breadcrumbs.push(bc));
        }
      } catch (error) {
        console.error(error);
      }

      breadcrumb = this.applyRouteDataToLabel(breadcrumb, data);

      const parentCategoryBreadcrumb = this.getParentCategoryBreadcrumb(data, url);
      if (parentCategoryBreadcrumb) {
        breadcrumbs.push(parentCategoryBreadcrumb);
      }

      if (url.includes('transpordi_info')) {
        breadcrumb.label = 'Transport';
      }

      breadcrumbs.push(breadcrumb);

      return this.getBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  private getParentCategoryBreadcrumb(data: any, url: string) {
    if (data?.breadcrumb !== 'category') return false;

    const category = data.data?.category;

    if (category && parseInt(category['level']) === 3 && category['parent']) {
      const urlParts = url.split('/');
      urlParts.pop();
      url = urlParts.join('/');

      let newUrl = category?.parent?.url_path;

      if (newUrl === 'brand') {
        newUrl = '/brands';
      } else {
        newUrl = '/category/' + newUrl;
      }

      const breadcrumb: IBreadcrumb = {
        label: category?.parent?.name,
        url: newUrl,
      };

      return breadcrumb;
    }

    return false;
  }

  private async getParentProductBreadcrumbs(data: RouteData): Promise<IBreadcrumb[]> {
    const lastCategory = this.categoryService.getLastCategory();

    if (data.breadcrumb !== 'product') {
      return [];
    }

    const productCategories = data?.product?.category as ProductCategory[];
    const breads: IBreadcrumb[] = [];

    if (lastCategory) {
      if (lastCategory.parent && Number.parseInt(lastCategory.parent.id) !== 2) {
        const parentCategoryBreadcrumb: IBreadcrumb = {
          label: lastCategory.parent.name,
          url: `category/${lastCategory.parent.url_path}`,
        };

        breads.push(parentCategoryBreadcrumb);
      }

      const currentCategoryBreadcrumb: IBreadcrumb = {
        label: lastCategory.name,
        url: `category/${lastCategory.url_path}`,
      };

      breads.push(currentCategoryBreadcrumb);

      if (productCategories) {
        productCategories.forEach((productCategory) => {
          const childrenId = lastCategory.children;
          const isIdsEqual =
            typeof childrenId === 'number'
              ? childrenId === productCategory.category_id
              : childrenId.includes(productCategory.category_id);

          if (isIdsEqual) {
            const subCategoryBreadcrumb: IBreadcrumb = {
              label: productCategory.name as string,
              params: { category_id: productCategory.category_id },
              url: 'false',
            };

            breads.push(subCategoryBreadcrumb);
          }
        });
      }
    } else if (productCategories && productCategories.length) {
      try {
        const categoriesByIdsBatch: BreadcrubmsInfoItem[] =
          await this.getCategoriesByIds(productCategories);

        const category: BreadcrubmsInfoItem | undefined = categoriesByIdsBatch.find(
          (cat) => Number(cat.level) === 2,
        );

        if (category) {
          const categoryBreadcrumb: IBreadcrumb = {
            label: category.name,
            params: { category_id: category.id },
            url: `category/${category.url_path}`,
          };

          breads.push(categoryBreadcrumb);

          const subCategory = categoriesByIdsBatch.find((cat) => {
            if (Array.isArray(category.children)) {
              return category.children.some((id) => Number(cat.id) === id);
            }

            return category.children === cat.id;
          });

          if (subCategory) {
            const subCategoryBreadcrumb: IBreadcrumb = {
              label: subCategory.name,
              params: { category_id: subCategory.id },
              url: `category/${subCategory.url_path}`,
            };

            breads.push(subCategoryBreadcrumb);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }

    for (const breadcrumb of breads) {
      if (breadcrumb.url === 'false') {
        try {
          const category = await lastValueFrom(
            this.categoryService.getCategoryById(breadcrumb.params?.category_id),
          );
          breadcrumb.url = `category/${category.url_path}`;
        } catch (error) {
          console.error(error);
        }
      }
    }
    return breads;
  }

  private getParentBlogBreadcrumb(data: any) {
    if (data['breadcrumbBlogParent'] == 'news') {
      const breadcrumb: IBreadcrumb = {
        label: 'News',
        url: '/news',
      };
      return breadcrumb;
    }
    if (data['breadcrumbBlogParent'] == 'portfolio') {
      const breadcrumb: IBreadcrumb = {
        label: 'Portfolio',
        url: '/portfolio',
      };
      return breadcrumb;
    }
    return false;
  }

  private applyRouteDataToLabel(breadcrumb: any, data: any) {
    if (breadcrumb.label === 'product' && data.product) {
      breadcrumb.label = Array.isArray(data.product.name)
        ? data.product.name[0]
        : data.product.name;
    }
    if (breadcrumb.label === 'category' && data.data && data.data.category) {
      breadcrumb.label = data.data.category.name;
      breadcrumb.url = `category/${data.data.category?.url_path}`;
    }
    if (breadcrumb.label === 'page' && data.page) {
      breadcrumb.label = data.page.title;
    }
    if (breadcrumb.label === 'post' && data.post) {
      breadcrumb.label = data.post.name;
    }

    return breadcrumb;
  }

  private getProductCategoriesIds(categories: ProductCategory[]): number[] {
    return categories.flatMap((cat) => cat.category_id);
  }

  private getCategoriesByIds(categories: ProductCategory[]) {
    const categoryIds = this.getProductCategoriesIds(categories);
    return lastValueFrom(this.categoryService.getCategoriesByIds(categoryIds));
  }
}
